<script lang="ts">
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import {
    createScheduleEvent,
    createScheduleEventsBatch,
    validateScheduleEventAction,
  } from "../../../../../../../../../../../../../../api/kehrblatt/schedule-events"
  import { ApiError } from "../../../../../../../../../../../../../../api/kehrblatt/client"
  import { confirmScheduleEventActionIssues } from "../../../../../../../../../../../../../../_shared/_logic/confirm-schedule-event-action-issues"
  import { buildBlockerCreatePayloads } from "./_logic/payload"
  import type {
    BlockerConfigurationOutput,
    BlockerCreateInit,
    BlockerDetailsOutput,
    BlockerGeneralOutput,
  } from "../../../../_shared/_logic/create-blocker-contract"
  import StepActionCTA from "../../../../../../../../../../../../_shared/_modules/step-action-cta"

  type Props = {
    init: BlockerCreateInit
    general: BlockerGeneralOutput
    configuration: BlockerConfigurationOutput
    details: BlockerDetailsOutput
    disabled?: boolean
    disabledReason?: string | null
    onCreated?: (events: ScheduleEvent[]) => Promise<void> | void
    onClose?: () => void
    onError?: (message: string | null) => void
  }

  const {
    init,
    general,
    configuration,
    details,
    disabled = false,
    disabledReason = null,
    onCreated = async () => {},
    onClose = () => {},
    onError = () => {},
  }: Props = $props()

  let saving = $state(false)

  const resolveErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof ApiError && error.message?.trim()) return error.message.trim()

    if (error instanceof Error && error.message?.trim()) return error.message.trim()

    return fallback
  }

  const validatePayload = async (payload: ScheduleEventCreatePayload) => {
    const validation = await validateScheduleEventAction({
      operation: "CREATE",
      surface: "CREATE_BLOCKER",
      create_payload: payload,
    })
    const issues = validation.issues

    if (!issues.length) return true

    return confirmScheduleEventActionIssues(issues, {
      operation: "CREATE",
      surface: "CREATE_BLOCKER",
      executionStrategy: "DIRECT_CREATE",
    })
  }

  const run = async () => {
    if (saving) return

    onError(null)

    if (disabled) {
      onError(disabledReason ?? "Bitte alle Pflichtfelder ausfüllen.")

      return
    }

    const result = buildBlockerCreatePayloads({
      init,
      general,
      configuration,
      details,
      now: new Date(),
    })

    if (result.ok === false) {
      onError(result.reason)

      return
    }

    saving = true
    try {
      const created =
        result.payloads.length === 1
          ? await (async () => {
              const confirmed = await validatePayload(result.payloads[0])

              if (!confirmed) return null

              return [await createScheduleEvent(result.payloads[0])]
            })()
          : (await createScheduleEventsBatch(result.payloads)).created

      if (!created) return

      const skippedParts: string[] = []

      if (result.skippedPast) skippedParts.push(`${result.skippedPast} in der Vergangenheit`)

      if (result.skippedConflicts) skippedParts.push(`${result.skippedConflicts} Kollisionen`)

      newNotification({
        class: "success",
        html: `${created.length} Einträge angelegt.${skippedParts.length ? ` Übersprungen: ${skippedParts.join(", ")}.` : ""}`,
      })
      await onCreated(created)
      onClose()
    } catch (error) {
      console.error("blocker_create_flow_failed", error)
      newNotification({
        class: "error",
        html: resolveErrorMessage(error, "Eintrag konnte nicht angelegt werden."),
      })
    } finally {
      saving = false
    }
  }
</script>

<StepActionCTA
  type="primaryButton"
  content={saving ? "Lege an…" : "Blocker anlegen"}
  disabled={saving || disabled}
  disabledReason={saving ? "Eintrag wird gerade angelegt." : disabledReason}
  onClick={run}
/>
